import { useState } from 'react';

import { FileType } from 'types';
import { useNavigate } from 'hooks';
import { Info } from 'components/info';
import { Flex } from 'components/flex';
import { Row, Col } from 'components/grid';
import { formatPrice } from 'utils/helpers';
import { Gallery } from 'components/gallery';
import { formatCalendarDate } from 'utils/date';
import { InfoBlock } from 'components/info-block';
import { Image, openReceipt } from 'components/image';
import { GeneralInfo } from 'components/general-info';
import { BackButton } from 'components/specified-buttons';
import { PieChartLegend } from 'components/pie-chart-legend';
import { Avatar, AvatarPlaceholderType } from 'components/avatar';

import * as Styles from './styles';
import { getCarRentalFields } from './car-rental';
import { AccountingExpensesDetailsProps } from './types';

const titles = ['Event', 'Amount', '% from total'];

export const AccountingExpensesDetails = ({
  data,
  hideEdit,
  disableSticky,
  hideBackButton,
}: AccountingExpensesDetailsProps) => {
  const navigate = useNavigate();
  const [isOpen, setOpen] = useState(false);
  const carRentalFields = getCarRentalFields(data);

  return (
    <>
      {!hideBackButton && <BackButton text="View Expense" />}

      <GeneralInfo
        withoutPhoto
        name="Expense"
        withPlaceholders
        stickyWithHeader={false}
        sectionWrapper={InfoBlock}
        disableSticky={disableSticky}
        containerStyles={{ gap: '16px' }}
        onEdit={!hideEdit ? () => navigate('edit') : undefined}
        sections={[
          {
            rows: [
              [
                {
                  col: 3,
                  colTablet: 6,
                  title: 'Payment Date',
                  value: formatCalendarDate(data?.paymentDate),
                },
                {
                  col: 3,
                  colTablet: 6,
                  title: 'Amount',
                  value: formatPrice(data?.amount),
                },
                {
                  col: 3,
                  colTablet: 6,
                  title: 'Categories',
                  value: data?.categories
                    ?.map((category) => category.name)
                    .join(', '),
                },
                {
                  col: 3,
                  colTablet: 6,
                  title: 'Payment Method',
                  value: data?.paymentMethod?.name,
                },
              ],
            ],
          },
        ]}
      />

      {carRentalFields && (
        // V32: inset like the Details card above (GeneralInfo's own 16px
        // padding), so both cards share the same edges and their columns
        // line up exactly.
        <Styles.DetailsAligned data-testid="expense-car-rental-card">
          <InfoBlock title="Car rental">
            <Row>
              {carRentalFields.map(({ title, value }) => (
                <Col col={3} key={title} colTablet={6}>
                  <Info title={title} value={value} />
                </Col>
              ))}
            </Row>
          </InfoBlock>
        </Styles.DetailsAligned>
      )}

      {data && data.categories.length > 1 && (
        <InfoBlock title="Category split">
          {data.categories.map((category) => (
            <Flex gap={16} key={category.id}>
              <span>{category.name}</span>
              <span>
                {category.ExpenseCategoryLink?.amount == null
                  ? 'Amount required'
                  : formatPrice(Number(category.ExpenseCategoryLink.amount))}
              </span>
            </Flex>
          ))}
        </InfoBlock>
      )}

      <PieChartLegend
        title="Events"
        titles={titles}
        mobileTitles={titles}
        data={
          data?.events.map((event) => ({
            label: event.amount,
            id: event.event.name,
            value: event.percent,
          })) || []
        }
      />

      <Flex gap={24} flexDirection="column">
        <InfoBlock title="Employees">
          <Row>
            {data?.employees?.map((employee) => (
              <Col col={6} key={employee.id}>
                <Flex gap={16}>
                  <Avatar
                    square
                    size={48}
                    url={employee?.user?.photoUrl}
                    userName={employee?.user?.name}
                    placeholder={AvatarPlaceholderType.UserAvatar}
                  />

                  <Flex flexDirection="column">
                    <p>{employee?.user?.name}</p>
                    <Styles.EventPercent>
                      {employee?.user?.role?.name}
                    </Styles.EventPercent>
                  </Flex>
                </Flex>
              </Col>
            ))}
          </Row>
        </InfoBlock>

        <InfoBlock title="Receipt and description">
          <Flex gap={16}>
            {data?.receiptUrl && (
              <Image
                width={136}
                height={136}
                objectFit="cover"
                alt="Receipt photo"
                src={data?.receiptUrl}
                onClick={() =>
                  openReceipt(data?.receiptUrl, () => setOpen(true))
                }
              />
            )}

            <p>{data?.description}</p>
          </Flex>
        </InfoBlock>
      </Flex>

      {data?.receiptUrl && (
        <Gallery
          open={isOpen}
          hideFooterButton
          close={() => setOpen(false)}
          files={[
            {
              id: 1,
              url: data.receiptUrl,
              type: FileType.Image,
              name: 'Receipt photo',
            },
          ]}
        />
      )}
    </>
  );
};
